import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { PROJECT_SUBMODULES } from '@qb/shared';

/** Submodules with a real view; the rest fall back to the "coming soon" placeholder. */
const SUBMODULE_VIEWS: Record<string, () => Promise<unknown>> = {
  approvals: () => import('@/views/ApprovalsView.vue'),
  elements: () => import('@/views/ElementsView.vue'),
  'work-releases': () => import('@/views/WorkReleasesView.vue'),
  structures: () => import('@/views/ProjectStructuresView.vue'),
  crafts: () => import('@/views/ProjectCraftsView.vue'),
};

/** One child route per project submodule. */
const projectSubmoduleRoutes: RouteRecordRaw[] = PROJECT_SUBMODULES.map((m) => ({
  path: m.slug,
  name: `project-${m.slug}`,
  component: SUBMODULE_VIEWS[m.slug] ?? (() => import('@/views/ProjectSubmoduleView.vue')),
  meta: { submodule: m.i18n, parent: 'projects' },
}));

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: {
      layout: 'blank',
      public: true,
    },
  },
  {
    path: '/',
    redirect: '/packages',
  },
  {
    path: '/packages',
    name: 'packages',
    component: () => import('@/views/PackagesView.vue'),
    meta: {
      breadcrumb: 'packages',
    },
  },
  {
    // Companies module.
    path: '/companies',
    name: 'companies',
    component: () => import('@/views/CompaniesView.vue'),
    meta: { breadcrumb: 'companies' },
  },
  {
    path: '/companies/new',
    name: 'companies-new',
    component: () => import('@/views/CompanyEditView.vue'),
    meta: { breadcrumb: 'createNew', parent: 'companies' },
  },
  {
    path: '/companies/:id/edit',
    name: 'companies-edit',
    component: () => import('@/views/CompanyEditView.vue'),
    meta: { breadcrumb: 'viewEdit', parent: 'companies' },
  },
  {
    // Projects module. Selecting a project opens its workspace (submodules).
    path: '/projects',
    name: 'projects',
    component: () => import('@/views/ProjectsView.vue'),
    meta: { breadcrumb: 'projects' },
  },
  {
    path: '/projects/new',
    name: 'projects-new',
    component: () => import('@/views/ProjectEditView.vue'),
    meta: { breadcrumb: 'createNew', parent: 'projects' },
  },
  {
    path: '/projects/:id/edit',
    name: 'projects-edit',
    component: () => import('@/views/ProjectEditView.vue'),
    meta: { breadcrumb: 'viewEdit', parent: 'projects' },
  },
  {
    // Project workspace shell — redirects to the first submodule and renders
    // each submodule into its nested <router-view>.
    path: '/projects/:id',
    name: 'project-workspace',
    component: () => import('@/views/ProjectWorkspaceView.vue'),
    meta: { breadcrumb: 'project', parent: 'projects' },
    redirect: (to) => ({ name: 'project-work-log', params: { id: to.params.id } }),
    children: projectSubmoduleRoutes,
  },
  {
    path: '/packages/new',
    name: 'packages-new',
    component: () => import('@/views/PackageEditView.vue'),
    meta: {
      breadcrumb: 'new',
      parent: 'packages',
    },
  },
  {
    path: '/packages/:id/edit',
    name: 'packages-edit',
    component: () => import('@/views/PackageEditView.vue'),
    meta: {
      breadcrumb: 'editPackage',
      parent: 'packages',
    },
  },
  {
    path: '/users',
    name: 'users',
    component: () => import('@/views/UsersView.vue'),
    meta: { breadcrumb: 'users', parent: 'usersManagement' },
  },
  {
    // User Edit/View form (Figma 10474:23873). Reached from the Users list
    // "Add email" dialog (?email=) and the row "View/Edit" action.
    path: '/users/new',
    name: 'users-new',
    component: () => import('@/views/UsersEditView.vue'),
    meta: { breadcrumb: 'new', parent: 'users' },
  },
  {
    path: '/users/:id/edit',
    name: 'users-edit',
    component: () => import('@/views/UsersEditView.vue'),
    meta: { breadcrumb: 'viewEdit', parent: 'users' },
  },
  {
    path: '/users/roles',
    name: 'roles',
    component: () => import('@/views/RolesView.vue'),
    meta: { breadcrumb: 'roles', parent: 'usersManagement' },
  },
  {
    path: '/users/roles/new',
    name: 'roles-new',
    component: () => import('@/views/RoleEditView.vue'),
    meta: { breadcrumb: 'new', parent: 'roles' },
  },
  {
    path: '/users/roles/:id/edit',
    name: 'roles-edit',
    component: () => import('@/views/RoleEditView.vue'),
    meta: { breadcrumb: 'editRole', parent: 'roles' },
  },
  {
    // Crafts-for-role page is opened from the role edit screen. Real
    // implementation will land in Stage 3; for now this routes to a
    // "Coming soon" placeholder so the button has a valid destination.
    path: '/users/roles/new/crafts',
    name: 'roles-crafts-new',
    component: () => import('@/views/RoleCraftsView.vue'),
    meta: { breadcrumb: 'rolesCrafts', parent: 'roles' },
  },
  {
    path: '/users/roles/:id/crafts',
    name: 'roles-crafts',
    component: () => import('@/views/RoleCraftsView.vue'),
    meta: { breadcrumb: 'rolesCrafts', parent: 'roles' },
  },
  {
    // System settings → Crafts catalog (Figma 7976:24587).
    path: '/system-settings/crafts',
    name: 'crafts',
    component: () => import('@/views/CraftsView.vue'),
    meta: { breadcrumb: 'crafts', parent: 'systemSettings' },
  },
  {
    // Craft create page (Figma 8023:42096).
    path: '/system-settings/crafts/new',
    name: 'crafts-new',
    component: () => import('@/views/CraftEditView.vue'),
    meta: { breadcrumb: 'createNew', parent: 'crafts' },
  },
  {
    path: '/system-settings/crafts/:id/edit',
    name: 'crafts-edit',
    component: () => import('@/views/CraftEditView.vue'),
    meta: { breadcrumb: 'viewEdit', parent: 'crafts' },
  },
  {
    // System settings → Standards (Figma 8023:145808).
    path: '/system-settings/standards',
    name: 'standards',
    component: () => import('@/views/StandardsView.vue'),
    meta: { breadcrumb: 'standards', parent: 'systemSettings' },
  },
  {
    // Standard create page (Figma 8023:145997).
    path: '/system-settings/standards/new',
    name: 'standards-new',
    component: () => import('@/views/StandardEditView.vue'),
    meta: { breadcrumb: 'createNew', parent: 'standards' },
  },
  {
    path: '/system-settings/standards/:id/edit',
    name: 'standards-edit',
    component: () => import('@/views/StandardEditView.vue'),
    meta: { breadcrumb: 'viewEdit', parent: 'standards' },
  },
  {
    // System settings → Professions (Figma 8023:161044).
    path: '/system-settings/professions',
    name: 'professions',
    component: () => import('@/views/ProfessionsView.vue'),
    meta: { breadcrumb: 'professions', parent: 'systemSettings' },
  },
  {
    path: '/system-settings/professions/new',
    name: 'professions-new',
    component: () => import('@/views/ProfessionEditView.vue'),
    meta: { breadcrumb: 'createNew', parent: 'professions' },
  },
  {
    path: '/system-settings/professions/:id/edit',
    name: 'professions-edit',
    component: () => import('@/views/ProfessionEditView.vue'),
    meta: { breadcrumb: 'viewEdit', parent: 'professions' },
  },
  {
    // System settings → Stages (identical to Professions).
    path: '/system-settings/stages',
    name: 'stages',
    component: () => import('@/views/StagesView.vue'),
    meta: { breadcrumb: 'stages', parent: 'systemSettings' },
  },
  {
    path: '/system-settings/stages/new',
    name: 'stages-new',
    component: () => import('@/views/StageEditView.vue'),
    meta: { breadcrumb: 'createNew', parent: 'stages' },
  },
  {
    path: '/system-settings/stages/:id/edit',
    name: 'stages-edit',
    component: () => import('@/views/StageEditView.vue'),
    meta: { breadcrumb: 'viewEdit', parent: 'stages' },
  },
  {
    // System settings → Positions (Figma 8023:162287). Create/edit is a modal.
    path: '/system-settings/positions',
    name: 'positions',
    component: () => import('@/views/PositionsView.vue'),
    meta: { breadcrumb: 'positions', parent: 'systemSettings' },
  },
  {
    // Fallback for not-yet-implemented sections of the Aside menu.
    path: '/:pathMatch(.*)*',
    name: 'placeholder',
    component: () => import('@/views/PlaceholderView.vue'),
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

/**
 * Auth guard. Imports the auth store lazily to avoid a circular dep
 * (auth store imports the router for redirects).
 */
router.beforeEach(async (to) => {
  // Public routes never need an auth check.
  if (to.meta?.public) return true;

  const { useAuthStore } = await import('@/stores/auth');
  const auth = useAuthStore();

  // Wait until the boot-time session check finishes — otherwise a refresh
  // could flash the login page before we know we have a valid token.
  if (!auth.ready) {
    await auth.restoreSession();
  }

  if (!auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }
  return true;
});
